import { useEffect } from 'react';

/**
 * Mobile chrome collapse (owner ask: content is ~60% of a phone screen).
 *
 * Watches a scroll source (window or a page's own scroll container) and
 * toggles `chrome-collapsed` on <html> while scrolling DOWN; first upward
 * scroll brings the chrome back. CSS does the rest — the app top bar and any
 * `.collapsible-chrome` block animate away, lifting content to ~80% of the
 * viewport while scanning lists. Desktop is untouched (mobile media query).
 */
const MOBILE_QUERY = '(max-width: 767px)';
const DELTA = 14; // hysteresis — ignore sub-14px jitters

export function useChromeCollapse(getScroller?: () => HTMLElement | null) {
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const el: HTMLElement | Window = getScroller?.() ?? window;
    const scrollTop = () =>
      el instanceof Window ? el.scrollY : el.scrollTop;

    let last = scrollTop();
    // Collapsing the chrome shrinks the document; the browser then clamps
    // scrollTop, which looks like a scroll-UP and would instantly reopen the
    // chrome (feedback loop). After each toggle, ignore deltas briefly and
    // resync the baseline once the height change settles.
    let lockUntil = 0;
    const toggle = (on: boolean) => {
      const html = document.documentElement;
      if (html.classList.contains('chrome-collapsed') === on) return;
      html.classList.toggle('chrome-collapsed', on);
      lockUntil = performance.now() + 400;
      window.setTimeout(() => { last = scrollTop(); }, 420);
    };
    const onScroll = () => {
      if (!mq.matches) return; // desktop keeps chrome fixed
      const now = performance.now();
      const y = scrollTop();
      if (now < lockUntil) { last = y; return; }
      const diff = y - last;
      if (Math.abs(diff) < DELTA) return;
      // Never collapse near the very top — chrome must be there on open.
      if (y < 40) toggle(false);
      else toggle(diff > 0);
      last = y;
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      document.documentElement.classList.remove('chrome-collapsed');
    };
  }, [getScroller]);
}

export default useChromeCollapse;
